import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './SubscriptionDocuments.module.scss';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import Modal from '../../../components/modal/Modal';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import { useDropzone } from 'react-dropzone';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';

const SubscriptionDocuments = ({ subscriptionId, documents = [], onRefresh }) => {
  const { showToast } = useToasts();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const onDrop = useCallback((accepted) => {
    setPendingFiles(prev => [...prev, ...accepted]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleClose = () => {
    setIsModalOpen(false);
    setPendingFiles([]);
  };

  const handleUpload = async () => {
    if (!pendingFiles.length) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      pendingFiles.forEach(file => formData.append('file', file));
      await axiosClient.post(`/subscription/${subscriptionId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast('Documents uploaded successfully');
      handleClose();
      await onRefresh();
    } catch (error) {
      showToast(error.message || 'Failed to upload documents', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axiosClient.delete(`/subscription/documents/${deletingId}`);
      showToast('Document deleted successfully');
      setDeletingId(null);
      await onRefresh();
    } catch (error) {
      showToast(error.message || 'Failed to delete document', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Documents</h2>
        <Button
          label={
            <div className={styles.cta}>
              <Icon name="arrow-up" />
              Upload Document
            </div>
          }
          variant="grey"
          size="small"
          onClick={() => setIsModalOpen(true)}
        />
      </div>

      {documents.length > 0 ? (
        <div className={styles.fileList}>
          {documents.map((doc) => (
            <div key={doc.id} className={styles.fileRow}>
              <div className={styles.fileIcon}>
                <Icon name="file-text" />
              </div>
              <span className={styles.fileName}>{doc.fileName}</span>
              <a
                href={doc.fileUrl}
                target="_blank"
                rel="noreferrer"
                className={styles.downloadBtn}
                title="Download"
              >
                <Icon name="download" />
              </a>
              <button
                className={styles.deleteBtn}
                onClick={() => setDeletingId(doc.id)}
                type="button"
                title="Delete"
              >
                <Icon name="trash" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className={styles.empty}>No documents uploaded yet.</p>
      )}

      {deletingId && (
        <Modal>
          <div className={styles.confirmModal}>
            {isDeleting && <LoadingOverlay />}
            <h2 className={styles.confirmTitle}>Are you sure you want to delete this document?</h2>
            <div className={styles.confirmButtons}>
              <Button label="Delete" size="small" variant="secondary" onClick={handleDelete} disabled={isDeleting} />
              <Button label="Cancel" size="small" variant="cancel" onClick={() => setDeletingId(null)} />
            </div>
          </div>
        </Modal>
      )}

      {isModalOpen && (
        <Modal>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Upload Documents</h3>
              <button className={styles.closeBtn} onClick={handleClose}>✕</button>
            </div>

            <div
              {...getRootProps()}
              className={`${styles.dropzone} ${isDragActive ? styles.dropzoneActive : ''}`}
            >
              <input {...getInputProps()} />
              <div className={styles.dropzoneContent}>
                <div className={styles.dropzoneIcon}>
                  <Icon name="file-text" />
                </div>
                <p className={styles.dropzoneText}>
                  Drag and drop files here or <span className={styles.dropzoneLink}>Choose Files</span>
                </p>
              </div>
            </div>

            {pendingFiles.length > 0 && (
              <div className={styles.pendingList}>
                {pendingFiles.map((file, index) => (
                  <div key={index} className={styles.pendingRow}>
                    <Icon name="file-text" />
                    <span className={styles.pendingName}>{file.name}</span>
                    <button
                      className={styles.removeBtn}
                      onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== index))}
                      type="button"
                    >
                      <Icon name="trash" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.modalActions}>
              <Button label="Cancel" variant="white" size="small" onClick={handleClose} />
              <Button
                label={isUploading ? 'Uploading...' : 'Upload'}
                variant="primary"
                size="small"
                onClick={handleUpload}
                disabled={!pendingFiles.length || isUploading}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

SubscriptionDocuments.propTypes = {
  subscriptionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  documents: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    fileUrl: PropTypes.string,
    fileName: PropTypes.string,
  })),
  onRefresh: PropTypes.func,
};

export default SubscriptionDocuments;
